import React, { useContext } from 'react';
import { WizardContext } from 'context/Context';
import { Alert } from 'react-bootstrap';
import { LayoutImage } from '../LayoutApproval';
import { ConfigContext } from 'context/Context';
import IconAlert from 'components/common/IconAlert';
import PropTypes from 'prop-types';
import { beforeToday } from 'si/helpers/beforeToday';

function windowRejected(type) {
  return type?.windowAnswered && !type?.windowConfirmed;
}

export function getTimeWindow(time) {
  const [hour, minute] = time.split(':');
  return `${make12Hour(time)} - ${make12Hour(
    `${parseInt(hour) + 3}:${minute}`
  )}`;
}

function make12Hour(time) {
  const [hour, minute] = time.split(':');
  const hour12 = hour % 12 || 12;
  return `${hour12}:${minute}${hour < 12 ? 'am' : 'pm'}`;
}

const PlumbingAlert = ({ plumbingInstall }) => {
  const { fabricator } = useContext(ConfigContext);
  return beforeToday(
    plumbingInstall?.date
  ) ? null : plumbingInstall?.reschedule ? (
    <Alert variant="secondary">
      We will contact you soon with a new date/time for your plumbing.
    </Alert>
  ) : (
    <Alert variant="secondary">
      Plumbing reconnect date: {plumbingInstall?.dateFormatted}
      {plumbingInstall?.time && (
        <>
          <br />
          Arrival window:{' '}
          <strong>{getTimeWindow(plumbingInstall?.time)}</strong>
        </>
      )}
      {plumbingInstall.windowConfirmed && (
        <p className="mb-0 mt-2" style={{ fontStyle: 'italic' }}>
          If you have an emergency that requires you to reschedule, please call{' '}
          <a href={fabricator.phonelink}>{fabricator.phone}</a>
        </p>
      )}
    </Alert>
  );
};

PlumbingAlert.propTypes = {
  plumbingInstall: PropTypes.object.isRequired
};

const TileAlert = ({ tileInstall }) => {
  const { fabricator } = useContext(ConfigContext);
  return beforeToday(tileInstall?.date) ? null : tileInstall?.reschedule ? (
    <Alert variant="secondary">
      We will contact you soon with a new date/time for your tile backsplash
      install.
    </Alert>
  ) : (
    <Alert variant="secondary">
      Tile backsplash install date: {tileInstall?.dateFormatted}
      {tileInstall?.time && (
        <>
          <br />
          Arrival window: <strong>{getTimeWindow(tileInstall?.time)}</strong>
        </>
      )}
      {tileInstall.windowConfirmed && (
        <p className="mb-0 mt-2" style={{ fontStyle: 'italic' }}>
          If you have an emergency that requires you to reschedule, please call{' '}
          <a href={fabricator.phonelink}>{fabricator.phone}</a>
        </p>
      )}
    </Alert>
  );
};

TileAlert.propTypes = {
  tileInstall: PropTypes.object.isRequired
};

const Confirmed = () => {
  const { fabricator } = useContext(ConfigContext);
  const {
    job,
    selectedRooms,
    decisionMaker,
    qaInfo,
    cooktopFollowUp,
    cabinetFollowUp,
    countFollowUps
  } = useContext(WizardContext);
  const { template, install /*, plumbingInstall*/ } = job;
  const qaComplete =
    qaInfo.satisfied || job?.voc?.activityComplete || job?.voc?.formComplete;
  const qaIssue = qaInfo?.contact === 'yes' && !job?.voc?.activityComplete;
  const qaFeedback = qaInfo?.contact === 'no' && !job?.voc?.activityComplete;

  // these 2 functions are DUPLICATED!
  function sinkNotPurchased() {
    return job?.sinkInfo?.purchased === 'not yet';
  }

  function sinkOnsite() {
    return (
      job?.sinkInfo?.purchased !== 'yes' ||
      (job?.sinkInfo?.purchased === 'yes' &&
        job?.sinkInfo?.arrival === 'on site')
    );
  }

  const faCompleteNoInstall = job?.faRelease && !job?.install;

  return (
    <>
      {template?.status === 'Approved' ||
      template?.status === 'Future' ||
      template?.status === 'In Progress' ? (
        windowRejected(template) ? (
          <>
            <p>
              We will contact you soon with a new date/time for your template
              measure.
            </p>
          </>
        ) : (
          <>
            <img
              className="img-fluid p-4"
              alt="Process Image"
              src="https://library.countertopwizard.com/OtherAsset%2Fcw-steps%2FMeasure.png"
            />
            <h4>Measure Confirmed</h4>
            <Alert variant="secondary">
              Measure date: <strong>{template?.dateFormatted}</strong>
              {template?.time && (
                <>
                  <br />
                  Arrival window:{' '}
                  <strong>{getTimeWindow(template?.time)}</strong>
                </>
              )}
            </Alert>
            {(template?.status === 'In Progress' ||
              template?.status === 'Future') &&
              template?.time &&
              template?.windowConfirmed && (
                <>
                  <IconAlert variant="warning">
                    If you have an emergency that requires you to reschedule,
                    please call{' '}
                    <a href={fabricator.phonelink}>{fabricator.phone}</a>
                  </IconAlert>
                </>
              )}
          </>
        )
      ) : install?.status === 'Approved' ||
        install?.status === 'Future' ||
        install?.status === 'In Progress' ? (
        windowRejected(install) ? (
          <>
            <p>
              We will contact you soon with a new date/time for your countertop
              install.
            </p>
          </>
        ) : (
          <>
            <h4>Install Confirmed</h4>
            <img
              className="img-fluid p-4"
              alt="Process Image"
              src="https://library.countertopwizard.com/OtherAsset%2Fcw-steps%2FInstall.png"
            />
            <Alert variant="secondary">
              Install date: <strong>{install?.dateFormatted}</strong>
              {install?.time && (
                <>
                  <br />
                  Arrival window:{' '}
                  <strong>{getTimeWindow(install?.time)}</strong>
                </>
              )}
            </Alert>
            {!install?.time && (
              <p>
                <em>
                  We will provide you with the time window of arrival 2 days
                  before the installation appointment.
                </em>
              </p>
            )}
            {(install?.status === 'In Progress' ||
              install?.status === 'Future') &&
              install?.time &&
              install?.windowConfirmed && (
                <>
                  <IconAlert variant="warning">
                    If you have an emergency that requires you to reschedule,
                    please call{' '}
                    <a href={fabricator.phonelink}>{fabricator.phone}</a>
                  </IconAlert>
                </>
              )}
            <p>
              Please ensure that you{' '}
              {decisionMaker?.name && <>or {decisionMaker?.name}</>} will be
              there at the time of our arrival and remain for the whole
              appointment.
            </p>

            {job?.plumbingInstall?.dateFormatted && (
              <PlumbingAlert plumbingInstall={job?.plumbingInstall} />
            )}

            {job?.tileInstallDetails?.dateFormatted && (
              <TileAlert tileInstall={job?.tileInstallDetails} />
            )}
          </>
        )
      ) : faCompleteNoInstall ? null : (
        <>
          {qaComplete && job?.plumbingInstall && (
            <PlumbingAlert plumbingInstall={job?.plumbingInstall} />
          )}
          {qaComplete && job?.tileInstallDetails && (
            <TileAlert tileInstall={job?.tileInstallDetails} />
          )}
          <h4>Thank you!</h4>
        </>
      )}
      {job.problem ? (
        <p>
          I'll give you a call soon to straighten things out and confirm the
          rest of your details.
        </p>
      ) : qaIssue ? (
        <>
          <p>
            We received your message and will respond to your issue as soon as
            possible.
          </p>
        </>
      ) : qaFeedback ? (
        <>
          <p>
            We appreciate your feedback and will use it to improve our service.
          </p>
        </>
      ) : qaComplete ? (
        <>
          <img
            className="img-fluid p-4"
            alt="Process Image"
            src="https://library.countertopwizard.com/OtherAsset%2Fcw-steps%2FComplete.png"
          />
          <p>Your countertops are installed 😃</p>
          <p>
            You'll receive emails from us shortly with care and maintenance
            instructions as well as your warranty information.
          </p>
          <Alert variant="secondary" className="pb-0">
            <p>
              You'll also be receiving a brief customer satisfaction survey from
              Home Depot to grade the quality of service you received
              specifically from Precision Countertops.
            </p>
            <p>
              If your crew did an excellent job for you (i.e., arrived within
              the expected timeframe, displayed a friendly, professional
              attitude, and cleaned up the work space upon their departure),
              please mark yourself as EXTREMELY SATISFIED.
            </p>
            <p>
              Please also provide any feedback you have about your crew in the
              Comment section of the survey as this sends an email to our
              management team here at Precision Countertops.
            </p>
          </Alert>

          <p>
            Thank you for choosing Home Depot and Precision Countertops, and
            don't hesitate to contact us with any questions or concerns.
            {job?.spsNumber && (
              <>
                {' '}
                Please reference your project ID# when communicating with us
                about your project: {job.spsNumber}
              </>
            )}
          </p>
        </>
      ) : install?.status === 'Approved' || install?.status === 'Future' ? (
        <>
          {/* <p>
            Your install date is confirmed for{' '}
            <strong>{install?.dateFormatted}</strong>
            {job?.plumbingInstall?.dateFormatted && (
              <>
                {' '}
                (and we will return {job.plumbingInstall.dateFormatted} to
                connect your plumbing)
              </>
            )}
            .
          </p> */}
          {/* {plumbingInstall && (
            <p>
              Your plumbing install date is confirmed for{' '}
              <strong>{plumbingInstall?.dateFormatted}</strong>.
            </p>
          )} */}
        </>
      ) : install?.status === 'Need New Date' ? (
        <p>
          Our schedulers are working on a new install date for you. We'll send
          you another text to confirm this date soon!
        </p>
      ) : install?.status === 'Will Call' ? (
        <p>
          I'll give you a call soon to find a date for your countertops install.
        </p>
      ) : install?.status === 'Ready To Send' ||
        install?.status === 'Pending Approval' ? (
        <p>
          We're working to schedule your countertops install and will get back
          to you soon with a date.
        </p>
      ) : install?.status === 'Installed' ? (
        <p>
          Your countertops installation is complete. We will follow up with you
          soon to make sure everything is as expected.
          <img
            className="img-fluid p-4"
            alt="Process Image"
            src="https://library.countertopwizard.com/OtherAsset%2Fcw-steps%2FComplete.png"
          />
        </p>
      ) : install?.status === 'In Progress' ? (
        <p>
          Your countertops installation is in progress. We will follow up with
          you soon to make sure everything is as expected.
        </p>
      ) : template?.status === 'Approved' || template?.status === 'Future' ? (
        <>
          {!template.time && (
            <p>
              <em>
                We will provide you with the time window of arrival 2 days prior
                to your appointment.
              </em>
            </p>
          )}
          {!windowRejected(template) && (
            <p>
              Please ensure that you{' '}
              {decisionMaker?.name && <>or {decisionMaker?.name}</>} will be
              there at the time of our arrival and remain for the whole
              appointment.
            </p>
          )}
        </>
      ) : template?.status === 'Need New Date' ? (
        <p>
          Our schedulers are working on a new measure date for you. We'll send
          you another text to confirm this date soon!
          <img
            className="img-fluid p-4"
            alt="Process Image"
            src="https://library.countertopwizard.com/OtherAsset%2Fcw-steps%2FMeasure.png"
          />
        </p>
      ) : template?.status === 'Will Call' ? (
        <p>
          I'll give you a call soon to find a date for your countertops measure.
        </p>
      ) : template?.status === 'Ready To Send' ||
        template?.status === 'Pending Approval' ? (
        <p>
          We're working to schedule your countertops measure and will get back
          to you soon with a date.
          <img
            className="img-fluid p-4"
            alt="Process Image"
            src="https://library.countertopwizard.com/OtherAsset/cw-steps/MeasureUpNext.png"
          />
        </p>
      ) : template?.status === 'Templated' && !faCompleteNoInstall ? (
        <>
          <p>
            Your countertops have been measured. We'll contact you soon to
            confirm your installation date.
          </p>
          {
            /* this is now under ConfirmedColor */ job?.digitalLayout &&
              job.digitalLayout.approved && (
                <>
                  <p>
                    Here is your approved digital layout (
                    <em>
                      please check your email for a record of your approval
                    </em>
                    ):
                  </p>
                  <LayoutImage />
                </>
              )
          }
          {
            /* this is now under ConfirmedColor */ job?.digitalLayout &&
              !job.digitalLayout.approved && (
                <Alert variant="secondary">
                  We will contact you to discuss your digital layout.
                </Alert>
              )
          }
        </>
      ) : template?.status === 'In Progress' ? (
        <p>
          Your countertops are in the process of being measured. We'll contact
          you soon to confirm your installation date.
        </p>
      ) : windowRejected(template) ? (
        <>
          <p>
            We will contact you soon with a new date/time for your template
            measure.
          </p>
        </>
      ) : job.cabinetsConfirmed ? (
        <>
          {countFollowUps() > 0 ? (
            <>
              <p>Thanks for confirming your countertop details.</p>
              <p>
                You have {countFollowUps()} outstanding item
                {countFollowUps() > 1 && 's'} to address in order to schedule
                your measure:
              </p>
              <ul className="my-2 mb-3">
                {(sinkNotPurchased() || !sinkOnsite()) && (
                  <li>
                    <a href="#confirm-sink">Update Sink Selection</a>
                    {job?.sinkInfo?.type && (
                      <em className="fs--1">
                        {' '}
                        - let us know when your sink has arrived
                      </em>
                    )}
                  </li>
                )}
                {cooktopFollowUp() && (
                  <li>
                    <a href="#confirm-cooktop">Update Cooktop/Range</a>
                    <em className="fs--1">
                      {' '}
                      - let us know when your cooktop/range has arrived
                    </em>
                  </li>
                )}
                {cabinetFollowUp() && (
                  <li>
                    <a href="#confirm-cabinets">Update Cabinets</a>
                    <em className="fs--1">
                      {' '}
                      - let us know when your cabinets have arrived
                    </em>
                  </li>
                )}
              </ul>
              {/* <p className="mt-3">
                After that's complete, we'll schedule your final measure
                appointment.
              </p> */}
            </>
          ) : faCompleteNoInstall ? null : (
            <>
              <p>
                Thanks for confirming your countertop details. I'll send you a
                text when your measure is scheduled!
              </p>
              <img
                className="img-fluid p-4"
                alt="Process Image"
                src="https://library.countertopwizard.com/OtherAsset/cw-steps/MeasureUpNext.png"
              />
            </>
          )}
        </>
      ) : selectedRooms && selectedRooms.length > 0 ? (
        <p>
          Thanks for confirming your material and splash details. I'll give you
          a call soon to cover the rest!
        </p>
      ) : (
        <>
          <p>
            Your contact information is confirmed, and we're reviewing your
            order. You'll receive another text in a day or two so you can
            confirm all of the details about your new countertops.
          </p>
          <img
            className="img-fluid p-4"
            alt="Process Image"
            src="https://library.countertopwizard.com/OtherAsset/cw-steps/ConfirmUpNext.png"
          />
        </>
      )}
      {job?.verifyOrder &&
        template?.status !== 'Templated' &&
        !install?.status &&
        countFollowUps() === 0 && (
          <Alert variant="secondary">
            Reminder:{' '}
            <strong>
              you need to have all your sinks, faucets, ranges, and cooktops on
              site
            </strong>{' '}
            when our technician comes to take final measurements for the digital
            template.
          </Alert>
        )}
      {!faCompleteNoInstall && (
        <>
          <p>
            If you have {(job.problem || qaIssue) && 'more'} questions, just
            reply to my text or call{' '}
            <a href={`tel:1-${job?.PC?.phone || '800-548-4445'}`}>
              {job?.PC?.phone || '800.548.4445'}
            </a>
            . I work M-F from 7a - 4p Pacific Time.
          </p>
          {job?.PC?.firstName && (
            <p style={{ fontStyle: 'italic' }}>-{job.PC.firstName}</p>
          )}
        </>
      )}
    </>
  );
};

export default Confirmed;
